import React, { useState, useCallback, useRef, useEffect } from 'react';
import { FlatList, KeyboardAvoidingView, Platform, ActivityIndicator, ScrollView } from 'react-native';
import { YStack } from '@tamagui/stacks';
import { Text, useThemeName } from '@tamagui/core';
import { useFocusEffect } from '@react-navigation/native';
import type { RootStackScreenProps } from '@/types/navigation';
import { Screen } from '@/components';
import ChatHeader from './components/ChatHeader';
import MessageBubble from './components/MessageBubble';
import DateSeparator from './components/DateSeparator';
import ChatInput from './components/ChatInput';
import MeetingInfo from './components/MeetingInfo';
import GiftSheet from './components/GiftSheet';
import { useMessages, useSendMessage, useMarkAsRead } from '@/hooks/useFirebaseChat';
import { useActiveChat } from '@/contexts/ActiveChatContext';
import { COLORS } from '@/constants/colors';

type Props = RootStackScreenProps<'ChatConversation'>;

const ChatConversationScreen = React.memo<Props>(({ route }) => {
  const { chatId, name, imageUri, meetingInfo } = route.params;
  const [inputText, setInputText] = useState('');
  const [giftSheetOpen, setGiftSheetOpen] = useState(false);
  const flatListRef = useRef<FlatList>(null);
  const themeName = useThemeName();
  const isDark = themeName === 'dark';

  const bgColor = isDark ? COLORS.BLACK : COLORS.WHITE;

  // Firebase hooks
  // useMessages automatically marks messages as read when new ones arrive (if chat is active)
  const { messages, isLoading, isLoadingMore, hasMore, loadMoreMessages, error } = useMessages(chatId);
  const { sendMessage, sendGift, isSending } = useSendMessage();
  const { markAsRead } = useMarkAsRead();
  const { setActiveChatId } = useActiveChat();

  // Set active chat when screen is focused and mark messages as read
  useFocusEffect(
    useCallback(() => {
      if (chatId) {
        setActiveChatId(chatId);
        // Mark as read when entering chat
        markAsRead(chatId);
      }
      
      return () => {
        // Mark as read when leaving chat, then clear active chat
        if (chatId) {
          markAsRead(chatId).then(() => {
            setActiveChatId(null);
          });
        } else {
          setActiveChatId(null);
        }
      };
    }, [chatId, setActiveChatId, markAsRead])
  );

  // With inverted FlatList, newest messages are automatically at the bottom
  // Scroll to bottom (top of inverted list) when new message is sent
  useEffect(() => {
    if (messages.length > 0) {
      flatListRef.current?.scrollToOffset({ offset: 0, animated: true });
    }
  }, [messages.length]);

  const handleSend = useCallback(async () => {
    if (!inputText.trim() || isSending || !chatId) return;

    const text = inputText.trim();
    setInputText(''); // Clear input immediately for better UX
    
    await sendMessage(chatId, text);
  }, [inputText, isSending, chatId, sendMessage]);

  const handleGiftPress = useCallback(() => {
    setGiftSheetOpen(true);
  }, []);

  const handleGiftSend = useCallback(async (giftData: {
    giftId: string;
    giftName: string;
    giftEmoji: string;
    amount: number;
  }) => {
    if (!chatId) return;
    
    await sendGift(chatId, giftData);
    setGiftSheetOpen(false);
  }, [chatId, sendGift]);

  const renderItem = useCallback(
    ({ item }: { item: typeof messages[0] }) => {
      if (item.date) {
        return <DateSeparator date={item.date} isDark={isDark} />;
      }

      return (
        <MessageBubble
          message={item.text}
          time={item.time}
          isOwn={item.isOwn}
          isDark={isDark}
          type={item.type || 'text'}
          giftData={item.giftData}
        />
      );
    },
    [isDark]
  );

  if (isLoading) {
    return (
      <Screen edges={['top', 'bottom']}>
        <YStack flex={1} backgroundColor={bgColor}>
          <ChatHeader name={name} imageUri={imageUri} isDark={isDark} />
          <YStack flex={1} justifyContent="center" alignItems="center">
            <ActivityIndicator size="large" color={COLORS.PRIMARY} />
            <Text color={isDark ? COLORS.WHITE : COLORS.DARK_TEXT} marginTop="$2">
              Ładowanie wiadomości...
            </Text>
          </YStack>
        </YStack>
      </Screen>
    );
  }

  if (error) {
    return (
      <Screen edges={['top', 'bottom']}>
        <YStack flex={1} backgroundColor={bgColor}>
          <ChatHeader name={name} imageUri={imageUri} isDark={isDark} />
          <YStack flex={1} justifyContent="center" alignItems="center" padding="$4">
            <Text color={COLORS.ERROR} textAlign="center">
              Nie udało się pobrać wiadomości
            </Text>
          </YStack>
        </YStack>
      </Screen>
    );
  }

  return (
    <Screen edges={['top', 'bottom']}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'padding'}
        style={{ flex: 1 }}
        keyboardVerticalOffset={0}
      >
        <YStack flex={1} backgroundColor={bgColor}>
          {/* Header */}
          <ChatHeader name={name} imageUri={imageUri} isDark={isDark} />

          {/* Meeting Info - temporarily hidden */}
          {/* {meetingInfo && (
            <MeetingInfo
              date={meetingInfo.date}
              time={meetingInfo.time}
              location={meetingInfo.location}
              amount={meetingInfo.amount}
              isDark={isDark}
            />
          )} */}

          {/* Messages List - inverted for automatic scroll to bottom */}
          <FlatList
            ref={flatListRef}
            data={[...messages].reverse()}
            keyExtractor={(item) => item.id}
            renderItem={renderItem}
            ItemSeparatorComponent={() => <YStack height={4} />}
            contentContainerStyle={{
              paddingHorizontal: 16,
              paddingVertical: 16,
              flexGrow: 1,
            }}
            showsVerticalScrollIndicator={false}
            inverted
            onEndReached={hasMore ? loadMoreMessages : undefined}
            onEndReachedThreshold={0.5}
            ListFooterComponent={
              isLoadingMore ? (
                <YStack padding="$2" alignItems="center" style={{ transform: [{ scaleY: -1 }] }}>
                  <ActivityIndicator size="small" color={COLORS.PRIMARY} />
                </YStack>
              ) : null
            }
            ListEmptyComponent={
              <YStack flex={1} justifyContent="center" alignItems="center" style={{ transform: [{ scaleY: -1 }] }}>
                <Text color={isDark ? COLORS.WHITE : COLORS.DARK_TEXT} opacity={0.6}>
                  Rozpocznij rozmowę!
                </Text>
              </YStack>
            }
          />

          {/* Chat Input */}
          <ChatInput
            value={inputText}
            onChangeText={setInputText}
            onSend={handleSend}
            onGiftPress={handleGiftPress}
            isDark={isDark}
            disabled={isSending}
          />
        </YStack>
      </KeyboardAvoidingView>

      {/* Gift Sheet */}
      <GiftSheet
        open={giftSheetOpen}
        onOpenChange={setGiftSheetOpen}
        isDark={isDark}
        onGiftSend={handleGiftSend}
      />
    </Screen>
  );
});

ChatConversationScreen.displayName = 'ChatConversationScreen';

export default ChatConversationScreen;
